import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";

import type {
  CopyActivityToDraftMutation,
  CopyActivityToDraftMutationVariables,
} from "@/graphql/graphql";
import { COPY_ACTIVITY_TO_DRAFT } from "@/pages/ActivityLibrary/queries";
import { useActivitiesListQuery } from "@/pages/UserDetails/components/CallPlans/CreateCallPlanModal/ActivitiesTable/useActivitiesListQuery";
import ActivityDialog from "@/pages/UserDetails/components/CallPlans/CreateCallPlanModal/ActivityDialog/ActivityDialog";
import { isErrorWithMessage } from "@/utils/errors";

import {
  editExistingActivityModalInteractiveCellSx,
  editExistingActivityModalPaperSx,
  editExistingActivityModalTableWrapSx,
} from "./EditExistingActivityModal.styles";

type EditExistingActivityModalProps = {
  open: boolean;
  onClose: () => void;
  onDraftForEdit: (draft: CopyActivityToDraftMutation["copyActivityToDraft"]) => void;
};

export const SelectExistingActivityForEditModal: React.FC<EditExistingActivityModalProps> = ({
  open,
  onClose,
  onDraftForEdit,
}) => {
  const [selectedActivityId, setSelectedActivityId] = useState<number>();

  const { titleSearchInput, setTitleSearchInput, activitiesLoading, activities, activitiesError } =
    useActivitiesListQuery({ skip: false });

  const [copyToDraft] = useMutation<
    CopyActivityToDraftMutation,
    CopyActivityToDraftMutationVariables
  >(COPY_ACTIVITY_TO_DRAFT);

  const prepareForActivityEdit = useCallback(
    async (activityId: number) => {
      if (!activityId) {
        return;
      }
      try {
        const copyToDraftResponse = await copyToDraft({ variables: { activityId } });
        if (!copyToDraftResponse.data) {
          return;
        }
        onDraftForEdit(copyToDraftResponse.data.copyActivityToDraft);
      } catch (error) {
        if (isErrorWithMessage(error) && error.message) {
          // TODO: post an error
          // console.error("Failed to create draft from activity: " + error.message);
        }
        return;
      }
    },
    [copyToDraft, onDraftForEdit]
  );

  if (!open) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={editExistingActivityModalPaperSx}>
        <Typography variant="h6">Edit existing activity</Typography>
        <TextField
          size="small"
          label="Search existing activities"
          value={titleSearchInput}
          onChange={(event) => setTitleSearchInput(event.target.value)}
          placeholder="Search by title"
        />

        {activitiesError && <Alert severity="error">Could not load activities.</Alert>}

        {!activitiesError && activitiesLoading && !activities.length && (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!activitiesError && !activitiesLoading && !activities.length && (
          <Typography variant="body2" color="text.secondary">
            No drafts found.
          </Typography>
        )}

        {!!activities.length && (
          <Box sx={editExistingActivityModalTableWrapSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity) => (
                  <TableRow key={activity.id} hover>
                    <TableCell
                      sx={editExistingActivityModalInteractiveCellSx}
                      onClick={() => setSelectedActivityId(activity.id)}
                    >
                      {activity.title}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={editExistingActivityModalInteractiveCellSx}
                      // onClick={() => setSelectedActivityId(activity.id)}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => prepareForActivityEdit(activity.id)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {selectedActivityId && (
          <ActivityDialog
            open={!!selectedActivityId}
            onClose={() => setSelectedActivityId(undefined)}
            activityId={selectedActivityId}
          />
        )}

        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
